'use client';

import { useTranslations } from 'next-intl';
import { AdjustableContactFormWithConfirmation } from '@/components/form/AdjustableContactFormWithConfirmation';
import { Modal } from '@/components/Modal';
import type { LocationDto } from '@/lib/api';
import { GaEventCategory } from '@/lib/gtm/gaEventCategory';
import { GaFormType } from '@/lib/gtm/gaFormType';
import { pushGaEvent } from '@/lib/gtm/pushGaEvent';
import { domainEntityOf } from '@/lib/render/domainEntity';
import { RenderContextProvider } from '@/lib/render/RenderContextProvider';
import { Text } from '@/lib/render/Text';
import type { HeaderContactModalConfig } from '@/types/menu';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  /** The Menu's `headerContactModal` block driving title and form. */
  config: HeaderContactModalConfig;
  /**
   * Current location, when the layout resolved one (Standort pages). Provides
   * the render-context fallbacks the Adjustable Contact Form uses:
   * `customerNumber`'s center-number prefix and the `locationSlug` fallback
   * (supersedes the former `centerNumber` prop plumbing from AM-SH-378).
   */
  location?: LocationDto;
  /** Brand placeholders, so `{domain.*}` resolves inside the modal too. */
  domainPlaceholders?: Record<string, string>;
  /**
   * The shell's resolved phone pair (70.07 chain), so `{domain.phone}` and
   * `{domain.matelsoPhoneLabel}` mean the same here as on the page behind the
   * modal — the location's own number on a Standort page, the Domain Setting's
   * elsewhere. A placeholder of the same name still overrides them, exactly as
   * it does in the page body (spec 70.11 § Resolution Order).
   */
  phone?: string;
  matelsoPhoneLabel?: string;
}

export const ContactModal = ({
  open,
  onClose,
  config,
  location,
  domainPlaceholders,
  phone,
  matelsoPhoneLabel,
}: ContactModalProps) => {
  const t = useTranslations();
  // `undefined` when neither the phone chain nor the placeholders have
  // anything, in which case the entity is left out entirely.
  const domain = domainEntityOf(
    { resolvedPhone: phone, resolvedMatelsoPhoneLabel: matelsoPhoneLabel },
    domainPlaceholders,
  );

  return (
    <Modal open={open} onClose={onClose} titleId="contact-modal-title">
      {!open ? null : (
        // The header mounts outside the page's block pipeline, so provide a
        // minimal render context: `AdjustableContactForm` reads
        // `entities.location`, and the success card's `confirmationBody`
        // widgets resolve their placeholders through it.
        <RenderContextProvider
          mode="live"
          tagBag={[]}
          renderable={{ kind: 'page', metadata: {}, blocks: [] }}
          entities={{
            ...(location ? { location } : {}),
            ...(domain ? { domain } : {}),
          }}
        >
          <h2
            id="contact-modal-title"
            className="mb-6 text-center text-2xl font-medium"
          >
            <Text>{config.title || t('contact.title')}</Text>
          </h2>

          <AdjustableContactFormWithConfirmation
            resolved={config.form}
            onSubmitAttempt={() =>
              // Legacy GA parity: the header contact form has always pushed
              // this event once validation passed, independent of any
              // CMS-configured tracking on the block.
              pushGaEvent({
                event_category: GaEventCategory.FormSubmissionInitiated,
                form_type: GaFormType.Kontaktformular,
              })
            }
          />
        </RenderContextProvider>
      )}
    </Modal>
  );
};
